import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft,
  ArrowRight,
  SkipForward,
  Search,
  Loader2,
} from 'lucide-react';
import { useTerritories, useApplications, useIndications } from '../hooks/useMasterData';
import { LIST_PARAMS, MODE_PARAMS } from '../utils/productListParams';
import WizardProgress from '../components/wizard/WizardProgress';
import WizardStep from '../components/wizard/WizardStep';
import Button from '../components/common/Button';
import FlagIcon from '../components/common/FlagIcon';
import { useTranslation } from '../i18n';
import { lucideIconByName } from '../utils/lucideIcons';

// ---------------------------------------------------------------------------
// Icon fallbacks (English name → Lucide icon name). Used only when the DB row
// has no `icon` value yet — a safety net for freshly imported master data.
// ---------------------------------------------------------------------------

const APPLICATION_ICON_FALLBACKS: Record<string, string> = {
  Beverage: 'CupSoda',
  'Effervescent Tablet': 'Sparkles',
  Granules: 'FlaskConical',
  Gummies: 'Candy',
  'Hard Capsule': 'Pill',
  Powder: 'FlaskConical',
  RTD: 'CupSoda',
  'Soft Capsule': 'Egg',
  Tablet: 'Tablets',
};

const INDICATION_ICON_FALLBACKS: Record<string, string> = {
  Antioxidant: 'ShieldPlus',
  Bloodstream: 'Activity',
  Bloodsugar: 'Scale',
  Bones: 'Bone',
  'Brain and nervous system': 'Brain',
  Cardiovascular: 'Heart',
  Children: 'Baby',
  Digestion: 'Apple',
  'E-Sports': 'Gamepad2',
  Ears: 'Ear',
  Energy: 'Zap',
  Eyes: 'Eye',
  'Genital & UTI': 'Syringe',
  'Hair & Nails': 'Sparkles',
  Immunity: 'ShieldPlus',
  'Inflammation/pain': 'Flame',
  Joints: 'Bone',
  'Liver health': 'Beaker',
  Longevity: 'Infinity',
  Malnutrition: 'Salad',
  'Men health': 'Dumbbell',
  'Metabolic syndrome': 'Activity',
  Mood: 'Moon',
  'Mouth & teeth': 'Smile',
  Pharmacokinetic: 'FlaskConical',
  Sexuality: 'Heart',
  Skin: 'Leaf',
  Sleep: 'Moon',
  Sport: 'Dumbbell',
  Stress: 'Moon',
  'Sun care': 'Sun',
  'Weight Management': 'Scale',
  'Women Health': 'Ribbon',
};

function renderIcon(iconName: string | null | undefined, fallbackName: string | undefined): React.ReactNode {
  const Icon = lucideIconByName(iconName) ?? lucideIconByName(fallbackName);
  return Icon ? <Icon size={24} /> : null;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

const OptionsSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
    {Array.from({ length: 8 }).map((_, i) => (
      <div
        key={i}
        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 animate-pulse"
      >
        <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="w-16 h-4 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// WizardPage
// ---------------------------------------------------------------------------

const WizardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const STEP_LABELS = [t.wizard.steps.country, t.wizard.steps.application, t.wizard.steps.indication];

  const { data: territories, isLoading: isLoadingTerritories } = useTerritories();
  const { data: applications, isLoading: isLoadingApplications } = useApplications();
  const { data: indications, isLoading: isLoadingIndications } = useIndications();

  const [currentStep, setCurrentStep] = useState(0);
  const [filterMode, setFilterMode] = useState<'and' | 'or'>('and');
  const [selectedTerritories, setSelectedTerritories] = useState<string[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [selectedIndications, setSelectedIndications] = useState<string[]>([]);

  const totalSteps = 3;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // ---- Options per step (built from API data) ----

  const territoryOptions = useMemo(
    () =>
      (territories ?? []).map((t) => ({
        value: String(t.id),
        label: t.name,
        icon: <FlagIcon code={t.code} size={24} />,
      })),
    [territories],
  );

  const applicationOptions = useMemo(
    () =>
      (applications ?? []).map((a) => ({
        value: String(a.id),
        label: a.name,
        icon: renderIcon(a.icon, APPLICATION_ICON_FALLBACKS[a.name]),
      })),
    [applications],
  );

  const indicationOptions = useMemo(
    () =>
      (indications ?? []).map((ind) => ({
        value: String(ind.id),
        label: ind.name,
        icon: renderIcon(ind.icon, INDICATION_ICON_FALLBACKS[ind.name]),
      })),
    [indications],
  );

  // ---- Current selection per step ----

  const selections = [selectedTerritories, selectedApplications, selectedIndications];
  const setters = [setSelectedTerritories, setSelectedApplications, setSelectedIndications];

  const currentOptions = [territoryOptions, applicationOptions, indicationOptions][currentStep];
  const currentSelection = selections[currentStep];
  const currentSetter = setters[currentStep];
  const isLoadingStep = [isLoadingTerritories, isLoadingApplications, isLoadingIndications][currentStep];

  const stepConfigs = [
    { title: t.wizard.selectCountry, subtitle: t.wizard.selectCountrySubtitle },
    { title: t.wizard.selectApplication, subtitle: t.wizard.selectApplicationSubtitle },
    { title: t.wizard.selectIndication, subtitle: t.wizard.selectIndicationSubtitle },
  ];

  // ---- Handlers ----

  const handleToggle = (value: string) => {
    currentSetter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleNext = () => {
    if (isLastStep) {
      navigateToResults();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    if (isLastStep) {
      navigateToResults();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const navigateToResults = () => {
    const params = new URLSearchParams();

    if (selectedTerritories.length > 0) {
      params.set(LIST_PARAMS.territories, selectedTerritories.join(','));
    }
    if (selectedApplications.length > 0) {
      params.set(LIST_PARAMS.applications, selectedApplications.join(','));
    }
    if (selectedIndications.length > 0) {
      params.set(LIST_PARAMS.indications, selectedIndications.join(','));
      params.set(MODE_PARAMS.indications, filterMode === 'and' ? 'AND' : 'OR');
    }

    const query = params.toString();
    navigate(query ? `/products?${query}` : '/products');
  };

  // ---- Render ----

  return (
    <div className="min-h-[calc(100vh-7rem)] flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-3xl flex flex-col flex-1">
        {/* Progress indicator */}
        <div className="mb-10">
          <WizardProgress
            currentStep={currentStep}
            totalSteps={totalSteps}
            labels={STEP_LABELS}
          />
        </div>

        {/* Step content */}
        <div className="flex-1 flex flex-col justify-center">
          {isLoadingStep ? (
            <div className="w-full">
              <div className="mb-8 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {stepConfigs[currentStep].title}
                </h2>
                <p className="mt-2 text-gray-500 dark:text-gray-400 text-sm sm:text-base">
                  {stepConfigs[currentStep].subtitle}
                </p>
              </div>
              <OptionsSkeleton />
              <div className="mt-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            </div>
          ) : (
            <WizardStep
              title={stepConfigs[currentStep].title}
              subtitle={stepConfigs[currentStep].subtitle}
              options={currentOptions}
              selectedValues={currentSelection}
              onToggle={handleToggle}
              headerExtra={currentStep > 0 ? (
                <>
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <span className={`text-sm font-medium transition-colors ${filterMode === 'and' ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500'}`}>
                      {t.wizard.filterAnd}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={filterMode === 'or'}
                      onClick={() => setFilterMode((prev) => (prev === 'and' ? 'or' : 'and'))}
                      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-vivatis-600 transition-colors duration-200 cursor-pointer"
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          filterMode === 'or' ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-sm font-medium transition-colors ${filterMode === 'or' ? 'text-vivatis-600 dark:text-vivatis-400' : 'text-gray-400 dark:text-gray-500'}`}>
                      {t.wizard.filterOr}
                    </span>
                  </div>
                  <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {filterMode === 'and'
                      ? t.wizard.filterAndExplanation
                      : t.wizard.filterOrExplanation}
                  </p>
                </>
              ) : undefined}
            />
          )}
        </div>

        {/* Bottom navigation buttons */}
        <div className="mt-10 flex flex-col-reverse items-center gap-3 border-t border-gray-200 dark:border-gray-800 pt-6 sm:flex-row sm:justify-between">
          {/* Left: Back button */}
          <div className="w-auto sm:w-28">
            {!isFirstStep && (
              <Button
                variant="secondary"
                onClick={handleBack}
                icon={<ArrowLeft size={16} />}
              >
                {t.common.back}
              </Button>
            )}
          </div>

          {/* Center: Skip */}
          <Button
            variant="ghost"
            onClick={handleSkip}
            icon={<SkipForward size={16} />}
          >
            {t.common.skip}
          </Button>

          {/* Right: Next / Show results */}
          <div className="w-auto">
            <Button
              variant="primary"
              onClick={handleNext}
              icon={
                isLastStep ? <Search size={16} /> : <ArrowRight size={16} />
              }
            >
              {isLastStep ? t.wizard.showResults : t.common.next}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardPage;
